import {DotFillIcon} from '@primer/octicons-react'
import {memo} from 'react'
import type {ConnectionStatus, SessionState} from '../types/session'
import {getLeadingVisualIconAttributes} from '../utils/get-leading-visual-icon-attributes'
import styles from './TaskLeadingVisual.module.css'

export interface TaskLeadingVisualProps {
  isOptimistic?: boolean
  prState?: string
  prInMergeQueue?: boolean
  sessionState: SessionState
  isArchived?: boolean
  isCLI?: boolean
  connectionStatus?: ConnectionStatus
  agentSlug?: string
  className?: string
}

/**
 * Displays a leading visual icon for a task/session based on its current state.
 *
 * This component is memoized to prevent animated icons (like CheckSpinner) from resetting
 * when other tasks in a list update.
 */
export const TaskLeadingVisual = memo(function TaskLeadingVisual({
  isOptimistic = false,
  prState,
  prInMergeQueue,
  sessionState,
  isArchived,
  isCLI,
  connectionStatus,
  agentSlug,
  className,
}: TaskLeadingVisualProps) {
  const iconAttributes = isOptimistic
    ? {icon: DotFillIcon, color: 'var(--fgColor-attention)', 'data-testid': 'icon-dot'}
    : getLeadingVisualIconAttributes(
        prState,
        prInMergeQueue,
        sessionState,
        isArchived,
        isCLI,
        connectionStatus,
        agentSlug,
      )

  const Icon = iconAttributes.icon

  return (
    <div
      className={`${styles.iconWrapper} ${className ?? ''}`}
      style={{
        color: iconAttributes.color,
        marginTop: sessionState === 'in_progress' && !isOptimistic && !isCLI ? '3px' : undefined,
      }}
      data-testid={iconAttributes['data-testid']}
    >
      <Icon />
    </div>
  )
})
